/**
 * useReactivePropertyManager - Bridge between ReactiveProperty system and existing property manager
 *
 * This hook creates a PropertyService instance that integrates with the existing
 * GraphQL-based usePropertyManager for database persistence.
 */

import { useCallback, useEffect } from 'react';
import { PropertyService } from './PropertyService';
import type { PropertyUpdate } from './PropertyService';
import { usePropertyManager, UsePropertyManagerReturn, PropertyUpdateEvent } from '../hooks/usePropertyManager';
import { useOptionalProjectPropertyManager } from '../hooks/ProjectPropertyContext';
import { PropertyType, PropertyValueData } from '../types';
import { propertyRegistry } from '../PropertyRegistry';
import { initializeGlobalPropertyService, getGlobalPropertyService, hasGlobalPropertyService, getGlobalBroadcastFunction, getGlobalBroadcastBatchFunction } from './globalPropertyService';
import { canPersistProjectWrites } from './projectWriteAccess';

/** Property scopes that are shared with everyone on the project; a read-only viewer must not persist them. */
const SHARED_PROPERTY_TYPES: ReadonlySet<PropertyType> = new Set([PropertyType.PROJECT_GLOBAL, PropertyType.ORGANIZATION]);
const isPersistable = (propertyType: PropertyType) => canPersistProjectWrites() || !SHARED_PROPERTY_TYPES.has(propertyType);

export interface UseReactivePropertyManagerOptions {
  projectId: string;
  organizationId?: string;
  objectIdentifiers?: string[];
}

/**
 * Hook that creates a PropertyService integrated with the existing property system
 * This also initializes the global service so Potree can access the same instance
 */
// Map of property managers keyed by projectId, so hooks with different projectIds
// (e.g. user settings with projectId='' vs project properties with an actual ID)
// don't overwrite each other's property manager reference.
const globalPropertyManagersByProject = new Map<string, ReturnType<typeof usePropertyManager>>();

/** Get the property manager for a specific projectId */
function getPropertyManagerForProject(pid: string): ReturnType<typeof usePropertyManager> | null {
  return globalPropertyManagersByProject.get(pid) ?? null;
}

/** Find ANY non-loading property manager that has a real projectId (for project-scoped reads) */
function findProjectPropertyManager(): ReturnType<typeof usePropertyManager> | null {
  for (const [pid, pm] of globalPropertyManagersByProject.entries()) {
    if (pid && !pm.loading) return pm;
  }
  return null;
}

/** Find ANY property manager with a real projectId, even if still loading (for writes/mutations) */
function findProjectPropertyManagerForWrite(): ReturnType<typeof usePropertyManager> | null {
  for (const [pid, pm] of globalPropertyManagersByProject.entries()) {
    if (pid) return pm;
  }
  return null;
}

/** Find any property manager that isn't loading and contains data for the given property */
function findPropertyManagerWithData(objectIdentifier: string, identifier: string, propertyType: PropertyType): ReturnType<typeof usePropertyManager> | null {
  for (const pm of globalPropertyManagersByProject.values()) {
    if (pm.loading) continue;
    const val = pm.getPropertyValue(objectIdentifier, identifier, propertyType);
    if (val !== undefined) return pm;
  }
  return null;
}

export const useReactivePropertyManager = (options: UseReactivePropertyManagerOptions): PropertyService & { loading: boolean } => {
  const { projectId, organizationId, objectIdentifiers } = options;

  // Callback to sync reactive properties when GraphQL data changes
  // This handles both initial load and subscription updates
  const handlePropertyUpdate = useCallback((event: PropertyUpdateEvent) => {
    if (!hasGlobalPropertyService()) return;

    const service = getGlobalPropertyService();
    // Only update if the property exists in the reactive system
    if (service.hasProperty(event.identifier, event.propertyIdentifier, event.propertyType)) {
      service.setValueFromRemote(event.identifier, event.propertyIdentifier, event.propertyType, event.value);
    }
  }, []);

  // Prefer the shared project-level property manager from context (single query for the whole project).
  // Fall back to a local usePropertyManager only when outside the provider or when objectIdentifiers are specified.
  const contextPropertyManager = useOptionalProjectPropertyManager();
  const useFiltered = objectIdentifiers && objectIdentifiers.length > 0;
  const canUseContext = !useFiltered && !!contextPropertyManager;
  const fallbackPropertyManager = usePropertyManager({
    projectId,
    organizationId,
    objectIdentifiers,
    onPropertyUpdate: handlePropertyUpdate,
    skip: canUseContext,
  });
  const propertyManager: UsePropertyManagerReturn = canUseContext ? contextPropertyManager : fallbackPropertyManager;

  // Register this propertyManager under its projectId so onLoad/onSave can find the right one
  globalPropertyManagersByProject.set(projectId, propertyManager);

  // Create service configuration
  const serviceConfig = {
    projectId,
    debounceMs: 500,

    // Save callback: Use existing updateProperty mutation
    onSave: async (identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData) => {
      // A viewer's change to shared state stays local: reported as saved so nothing complains.
      if (!isPersistable(propertyType)) return true;
      try {
        const config = propertyRegistry.getConfig(identifier);
        if (!config) {
          console.error(`Cannot save: Property "${identifier}" not registered`);
          return false;
        }

        // Find the right property manager based on property type:
        // - PROJECT_GLOBAL/PROJECT_USER need a manager with a real projectId
        // - USER_SETTINGS/ORGANIZATION can use any manager
        // For writes, use findProjectPropertyManagerForWrite which doesn't require
        // the manager to be done loading (mutations only need the projectId, not loaded data).
        let pm: ReturnType<typeof usePropertyManager> | null = null;
        if (propertyType === PropertyType.PROJECT_GLOBAL || propertyType === PropertyType.PROJECT_USER) {
          pm = findProjectPropertyManager() ?? findProjectPropertyManagerForWrite();
        }
        pm = pm ?? getPropertyManagerForProject(projectId) ?? findProjectPropertyManagerForWrite();
        if (!pm) {
          console.error('PropertyManager not available');
          return false;
        }

        await pm.updateProperty({
          identifier: identifier, // Property name (e.g., 'surface_plane_position')
          propertyIdentifier: objectIdentifier, // Object/surface ID
          value,
          valueType: config.valueType,
          propertyType,
        });

        return true;
      } catch (error) {
        console.error(`Error saving property ${identifier}:`, error);
        return false;
      }
    },

    // Batch save callback: Use existing updatePropertiesBatch mutation
    onSaveBatch: async (allUpdates: Array<{ identifier: string; objectIdentifier: string; value: PropertyValueData; propertyType: PropertyType }>) => {
      const updates = allUpdates.filter((update) => isPersistable(update.propertyType));
      if (!updates.length) return true;
      try {
        // Batch updates are typically for project-scoped properties, so prefer a project manager
        const pm = findProjectPropertyManager()
          ?? findProjectPropertyManagerForWrite()
          ?? getPropertyManagerForProject(projectId);
        if (!pm) {
          console.error('PropertyManager not available');
          return false;
        }

        // Convert PropertyUpdate[] to PropertyUpdateInput[]
        const propertyUpdates = updates.map((update) => {
          const config = propertyRegistry.getConfig(update.identifier);
          if (!config) {
            throw new Error(`Property "${update.identifier}" not registered`);
          }

          return {
            identifier: update.identifier,
            propertyIdentifier: update.objectIdentifier,
            value: update.value,
            valueType: config.valueType,
            propertyType: update.propertyType,
          };
        });

        await pm.updatePropertiesBatch(propertyUpdates);
        return true;
      } catch (error) {
        console.error('Error in batch save:', error);
        return false;
      }
    },

    // Load callback: Use existing getPropertyValue query (synchronous)
    onLoad: (identifier: string, objectIdentifier: string, propertyType: PropertyType) => {
      try {
        // For project-scoped properties, prefer a manager with a real projectId
        const isProjectScoped = propertyType === PropertyType.PROJECT_GLOBAL || propertyType === PropertyType.PROJECT_USER;
        const primaryPm = isProjectScoped ? (findProjectPropertyManager() ?? getPropertyManagerForProject(projectId)) : (getPropertyManagerForProject(projectId) ?? findProjectPropertyManager());

        if (primaryPm && !primaryPm.loading) {
          const value = primaryPm.getPropertyValue(objectIdentifier, identifier, propertyType);
          if (value !== undefined) return value;
        }

        // Fallback: search all managers for one that has data for this property
        const fallbackPm = findPropertyManagerWithData(objectIdentifier, identifier, propertyType);
        if (fallbackPm) {
          return fallbackPm.getPropertyValue(objectIdentifier, identifier, propertyType);
        }

        // If the primary manager is still loading, return undefined (will be loaded later by useEffect)
        if (primaryPm?.loading) return undefined;

        return undefined;
      } catch (error) {
        console.error(`Error loading property ${identifier}:`, error);
        return undefined;
      }
    },

    // Immediate change callback: Broadcast to other clients via Pusher (no debounce)
    onImmediateChange: (identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData) => {
      const broadcastFn = getGlobalBroadcastFunction();
      broadcastFn?.(identifier, objectIdentifier, propertyType, value);
    },

    // Batch broadcast callback: sends one Pusher event for all changes in a batchUpdate call
    onImmediateChangeBatch: (updates: PropertyUpdate[]) => {
      const batchBroadcastFn = getGlobalBroadcastBatchFunction();
      batchBroadcastFn?.(updates);
    },

    // Flush pending saves on page unload via sendBeacon (sync-safe)
    onBeforeUnloadFlush: (updates: PropertyUpdate[]) => {
      const serverUrl = import.meta.env.VITE_NX_SERVER;
      if (!serverUrl) return;

      // Group updates by property type and send appropriate batch mutations
      const projectUpdates = updates.filter((u) => u.propertyType === PropertyType.PROJECT_USER || u.propertyType === PropertyType.PROJECT_GLOBAL);
      const orgUpdates = updates.filter((u) => u.propertyType === PropertyType.ORGANIZATION);
      const userUpdates = updates.filter((u) => u.propertyType === PropertyType.USER_SETTINGS);

      const mutations: string[] = [];
      const variables: Record<string, unknown> = {};

      if (projectUpdates.length > 0 && projectId) {
        mutations.push(`m0: projectBatchupdateProperties(projectId: $projectId, updates: $projectUpdates) { id }`);
        variables.projectId = projectId;
        variables.projectUpdates = projectUpdates.map((u) => {
          const config = propertyRegistry.getConfig(u.identifier);
          return { identifier: u.identifier, propertyIdentifier: u.objectIdentifier, value: u.value, valueType: config?.valueType, propertyType: u.propertyType };
        });
      }

      if (orgUpdates.length > 0 && organizationId) {
        mutations.push(`m1: organizationBatchUpdateProperties(organizationId: $organizationId, updates: $orgUpdates)`);
        variables.organizationId = organizationId;
        variables.orgUpdates = orgUpdates.map((u) => {
          const config = propertyRegistry.getConfig(u.identifier);
          return { identifier: u.identifier, propertyIdentifier: u.objectIdentifier, value: u.value, valueType: config?.valueType };
        });
      }

      if (userUpdates.length > 0) {
        mutations.push(`m2: userBatchUpdateProperties(updates: $userUpdates)`);
        variables.userUpdates = userUpdates.map((u) => {
          const config = propertyRegistry.getConfig(u.identifier);
          return { identifier: u.identifier, propertyIdentifier: u.objectIdentifier, value: u.value, valueType: config?.valueType };
        });
      }

      if (mutations.length === 0) return;

      // Build variable declarations for the GraphQL query
      const varDecls: string[] = [];
      if (variables.projectId !== undefined) {
        varDecls.push('$projectId: ID!', '$projectUpdates: [PropertyUpdateInput!]!');
      }
      if (variables.organizationId !== undefined) {
        varDecls.push('$organizationId: ID!', '$orgUpdates: [PropertyUpdateInput!]!');
      }
      if (variables.userUpdates !== undefined) {
        varDecls.push('$userUpdates: [PropertyUpdateInput!]!');
      }

      const query = `mutation flushProperties(${varDecls.join(', ')}) { ${mutations.join(' ')} }`;
      const payload = JSON.stringify({ query, variables });

      navigator.sendBeacon(
        `${serverUrl}/graphql?t=mutation&o=flushProperties`,
        new Blob([payload], { type: 'application/json' }),
      );
    },
  };

  // Initialize global service during render (before effects run)
  // This ensures the service is available immediately for syncSurfaces
  // Always re-initialize to ensure correct config (handles HMR and projectId changes)
  if (!hasGlobalPropertyService()) {
    initializeGlobalPropertyService(serviceConfig);
  }

  // Get the service (it should always be initialized at this point)
  const service = getGlobalPropertyService();

  if (!service) {
    throw new Error('PropertyService failed to initialize');
  }

  // Flush pending property saves when the user closes/navigates away from the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasGlobalPropertyService()) {
        getGlobalPropertyService().flushPendingSavesSync();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Note: We don't cleanup the global service on unmount since it's shared
  // It will be cleaned up when the app unmounts or page reloads

  // Bulk preloading of property values is done once in ProjectPropertyProvider,
  // not here. This hook can be instantiated by many components; running preload
  // in each instance caused stale GraphQL data to overwrite runtime property
  // changes whenever a component mounted (e.g. on selection change).

  // Return the service instance with loading state
  return Object.assign(service, { loading: propertyManager.loading });
};
